/**
 * FirmaBelgeGereksinimleriPage.jsx — "Rollere göre gerekli belgeler" (backlog #318, 2026-10-11).
 *
 * Seçili fuar için portalın altı arayüz setinde (Yerli / Yabancı × Katılımcı / Dekoratör / Organizatör)
 * hangi firma belgesinin ve hangi personel belgesinin GEREKLİ, İSTEĞE BAĞLI ya da UYGULANMAZ olduğu.
 * Kendi hesabı yok: GET /portal/belge-gereksinimleri (backend #314, BelgeGereksinimService) kart
 * kontrolünün okuduğu kaynaklardan hesaplıyor, sayfa yalnızca çiziyor — kural değişince sayfa da değişir.
 *
 * Firma girişinde firmanın kendi setleri vurgulanır (portal bağlamı); personel tümünü düz görür.
 * Rota side "any": iki taraf da açar.
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Card, CardBody, CardHeader, Container, Spinner, Table } from "reactstrap";
import { FileCheck2, Check, Minus, CircleDashed } from "lucide-react";
import MainContext from "../../Components/Contexts/MainContext";
import { usePortal } from "../../Components/Contexts/PortalContext";
import { getBelgeGereksinimleri } from "../../services/portalService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const DURUM = {
  GEREKLI: { renk: "success", get ad() { return i18n.t("portal:belgeGereksinimleri.gerekli"); }, ikon: <Check size={14} /> },
  ISTEGE_BAGLI: { renk: "warning", get ad() { return i18n.t("portal:belgeGereksinimleri.istegeBagli"); }, ikon: <CircleDashed size={14} /> },
  UYGULANMAZ: { renk: "light", get ad() { return i18n.t("portal:belgeGereksinimleri.uygulanmaz"); }, ikon: <Minus size={14} /> },
};

function Hucre({ durum, not }) {
  const d = DURUM[durum] ?? DURUM.UYGULANMAZ;
  return (
    <td className="text-center" title={not || d.ad}>
      <Badge color={d.renk} className={d.renk === "light" ? "text-muted border" : undefined} pill>
        {d.ikon} <span className="ms-1">{d.ad}</span>
      </Badge>
    </td>
  );
}

export default function FirmaBelgeGereksinimleriPage() {
  const { t } = useTranslation("portal");
  const { selectedFuarId } = useContext(MainContext);
  const portal = usePortal();
  const fuarId = portal?.fuarId ?? selectedFuarId;
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);

  useEffect(() => { document.title = i18n.t("portal:belgeGereksinimleri.gerekliBelgelerFds"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setYukleniyor(true);
      setHata(null);
      try {
        const out = await getBelgeGereksinimleri(fuarId);
        if (!ignore) setVeri(out);
      } catch (e) {
        if (!ignore) setHata(e.message);
      } finally {
        if (!ignore) setYukleniyor(false);
      }
    }
    if (fuarId) run();
    return () => { ignore = true; };
  }, [fuarId]);

  const setler = useMemo(() => veri?.arayuzler ?? [], [veri]);
  const kendi = useMemo(() => new Set((portal?.arayuzler ?? []).map((a) => a.kod)), [portal]);
  const aktifKod = portal?.aktif?.kod;

  // Satırlar: firma belgeleri (grup grup), fuarın ek firma belgeleri, personel belgeleri, kimlik doğrulama.
  const firmaSatirlari = useMemo(() => {
    const ilk = setler[0];
    if (!ilk) return [];
    return ilk.firmaBelgeleri.map((b) => ({
      anahtar: `${b.grup}-${b.fileTypeId}`,
      grupAdi: b.grupAdi,
      ad: b.ad,
      fuaraOzel: b.fuaraOzel,
      hucreler: setler.map((s) => s.firmaBelgeleri.find((x) => x.fileTypeId === b.fileTypeId && x.grup === b.grup)),
    }));
  }, [setler]);

  // #430: what the fair requires differs by the COMPANY's nationality now (a foreign company is not asked for what it cannot upload): one row per document any set requires
  const fuarSatirlari = useMemo(() => {
    const gorulen = new Map();
    setler.forEach((s) => (s.fuarBelgeleri ?? []).forEach((b) => { if (!gorulen.has(b.fileTypeId)) gorulen.set(b.fileTypeId, b.ad); }));
    return [...gorulen.entries()].map(([fileTypeId, ad]) => ({
      fileTypeId, ad,
      hucreler: setler.map((s) => ((s.fuarBelgeleri ?? []).some((x) => x.fileTypeId === fileTypeId) ? "GEREKLI" : "UYGULANMAZ")),
    }));
  }, [setler]);

  // #430: with "kişinin uyruğu esas" ON the personal rows are a T.C. worker's and each set carries what a FOREIGN worker owes beside them
  const uyrukEsasi = veri?.uyrukEsasi === true;

  const kolonSinifi = (kod) => (kod === aktifKod ? "table-primary" : kendi.has(kod) ? "table-info" : undefined);

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
            <FileCheck2 size={18} className="text-muted" />
            <h5 className="card-title mb-0 flex-grow-1">{t("belgeGereksinimleri.rollereGoreGerekliBelgeler")}</h5>
            {yukleniyor && <Spinner size="sm" />}
          </CardHeader>
          <CardBody>
            {!fuarId && <Alert color="warning" className="small">{t("belgeGereksinimleri.onceBasliktanBirFuarSecin")}</Alert>}
            {hata && <Alert color="danger" className="small">{hata}</Alert>}
            {portal?.aktif && (
              <p className="small text-muted">
                {t("belgeGereksinimleri.sizinArayuzunuz")}{" "}<strong>{portal.aktif.ad}</strong>{" "}{t("belgeGereksinimleri.maviSutun")}
                {portal.arayuzler.length > 1 && t("belgeGereksinimleri.firmanizinBuFuardakiDigerSetleri")}
              </p>
            )}
            {setler.length > 0 && (
              <div className="table-responsive">
                <Table size="sm" bordered className="align-middle mb-3">
                  <thead className="table-light">
                    <tr>
                      <th style={{ minWidth: 220 }}>{t("belgeGereksinimleri.belge")}</th>
                      {setler.map((s) => (
                        <th key={s.kod} className={`text-center ${kolonSinifi(s.kod) ?? ""}`} style={{ minWidth: 120 }}>
                          {s.ad}
                          <div className="small text-muted fw-normal">{t("belgeGereksinimleri.kartYolu")}{" "}{s.kartYolu}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="table-secondary"><th colSpan={setler.length + 1} className="small">{t("belgeGereksinimleri.firmaBelgeleri")}</th></tr>
                    {firmaSatirlari.map((r) => (
                      <tr key={r.anahtar}>
                        <td className="small">
                          {r.ad}
                          <div className="text-muted" style={{ fontSize: 11 }}>{r.grupAdi}{r.fuaraOzel ? t("belgeGereksinimleri.fuaraOzel") : ""}</div>
                        </td>
                        {r.hucreler.map((h, i) => <Hucre key={setler[i].kod} durum={h?.durum} not={h?.not} />)}
                      </tr>
                    ))}
                    {/* WAS (until #430): setler[0].fuarBelgeleri, every cell GEREKLI: one list for every set */}
                    {fuarSatirlari.length > 0 && (
                      <>
                        <tr className="table-secondary"><th colSpan={setler.length + 1} className="small">{t("belgeGereksinimleri.buFuarinIstedigiEkFirma")}</th></tr>
                        {fuarSatirlari.map((b) => (
                          <tr key={`fuar-${b.fileTypeId}`} data-testid="fuar-belge-satiri">
                            <td className="small">{b.ad}</td>
                            {b.hucreler.map((durum, i) => <Hucre key={setler[i].kod} durum={durum} />)}
                          </tr>
                        ))}
                      </>
                    )}
                    <tr className="table-secondary"><th colSpan={setler.length + 1} className="small">{uyrukEsasi ? t("belgeGereksinimleri.personelBelgeleriYerli") : t("belgeGereksinimleri.personelBelgeleri")}</th></tr>
                    {(veri.personelBelgeKatalogu ?? []).map((b) => (
                      <tr key={`kisi-${b.fileTypeId}`}>
                        <td className="small">{b.ad || b.key}</td>
                        {setler.map((s) => {
                          const h = s.personelBelgeleri.find((x) => x.fileTypeId === b.fileTypeId);
                          return <Hucre key={s.kod} durum={h?.durum} />;
                        })}
                      </tr>
                    ))}
                    {uyrukEsasi && (
                      <>
                        <tr className="table-secondary"><th colSpan={setler.length + 1} className="small" data-testid="yabanci-kisi-baslik">{t("belgeGereksinimleri.personelBelgeleriYabanci")}</th></tr>
                        {(veri.personelBelgeKatalogu ?? []).map((b) => (
                          <tr key={`yabanci-kisi-${b.fileTypeId}`} data-testid="yabanci-kisi-satiri">
                            <td className="small">{b.ad || b.key}</td>
                            {setler.map((s) => {
                              const h = s.yabanciKisi?.personelBelgeleri?.find((x) => x.fileTypeId === b.fileTypeId);
                              return <Hucre key={s.kod} durum={h?.durum} />;
                            })}
                          </tr>
                        ))}
                      </>
                    )}
                    <tr>
                      <td className="small">{t("belgeGereksinimleri.kimlikDogrulamaSaka")}</td>
                      {setler.map((s) => (
                        <Hucre key={s.kod} durum={s.kimlikDogrulama?.durum}
                               not={(s.kimlikDogrulama?.belgeler ?? []).join(" / ")} />
                      ))}
                    </tr>
                    <tr>
                      <td className="small">{t("belgeGereksinimleri.isgPersoneliVeIsgBelgeleri")}</td>
                      {setler.map((s) => <Hucre key={s.kod} durum={s.isgPersoneli ? "GEREKLI" : "UYGULANMAZ"} />)}
                    </tr>
                    <tr>
                      <td className="small text-muted">{t("belgeGereksinimleri.firmaninKartKosulu")}</td>
                      {setler.map((s) => <td key={s.kod} className="small text-center text-muted">{s.firmaKartKosulu}</td>)}
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}
            {setler.length > 0 && (
              <p className="small text-muted" data-testid="uyruk-esasi-notu">
                {uyrukEsasi ? t("belgeGereksinimleri.uyrukEsasiAcik") : t("belgeGereksinimleri.uyrukEsasiKapali")}
              </p>
            )}
            {(veri?.notlar ?? []).length > 0 && (
              <ul className="small text-muted mb-0">
                {veri.notlar.map((n) => <li key={n}>{n}</li>)}
              </ul>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
}
